import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { emailPreferencesService } from '../../services/emailPreferencesService';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';

const EmailNotificationPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const frequencyOptions = [
    { value: 'immediate', label: 'Immediate', description: 'Receive emails instantly' },
    { value: 'daily', label: 'Daily Digest', description: 'Once per day summary' },
    { value: 'weekly', label: 'Weekly Digest', description: 'Once per week summary' },
    { value: 'never', label: 'Never', description: 'No emails for this category' }
  ];

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await emailPreferencesService?.getPreferences(user?.id);
      setPreferences(data);
    } catch (err) {
      setError(err?.message || 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (field) => {
    if (!preferences) return;
    
    const newValue = !preferences?.[field];
    const updatedPreferences = { ...preferences, [field]: newValue };
    setPreferences(updatedPreferences);

    try {
      await emailPreferencesService?.updatePreferences(user?.id, { [field]: newValue });
      setSuccess('Preference updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to update preference');
      // Revert on error
      setPreferences(preferences);
    }
  };

  const handleFrequencyChange = async (field, value) => {
    if (!preferences) return;
    
    const updatedPreferences = { ...preferences, [field]: value };
    setPreferences(updatedPreferences);

    try {
      await emailPreferencesService?.updatePreferences(user?.id, { [field]: value });
      setSuccess('Frequency updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to update frequency');
      // Revert on error
      setPreferences(preferences);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await emailPreferencesService?.updatePreferences(user?.id, preferences);
      setSuccess('All preferences saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Icon name="Loader" size={32} className="animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
            <Icon name="Mail" size={24} className="text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Email Notification Preferences</h1>
            <p className="text-sm text-muted-foreground">Configure which emails you receive and how often</p>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-success/10 border border-success rounded-lg p-4 flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <p className="text-sm text-success">{success}</p>
          </div>
        )}

        {error && (
          <div className="bg-error/10 border border-error rounded-lg p-4 flex items-center gap-3">
            <Icon name="AlertCircle" size={20} className="text-error" />
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {/* Transaction Emails */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="CreditCard" size={20} className="text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Transaction Emails</h3>
                  <p className="text-sm text-muted-foreground">Payment confirmations, receipts, and transaction updates</p>
                </div>
                <button
                  onClick={() => handleToggle('transactionEmails')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences?.transactionEmails ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences?.transactionEmails ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {preferences?.transactionEmails && (
                <div className="mt-4">
                  <Select
                    label="Notification Frequency"
                    options={frequencyOptions}
                    value={preferences?.transactionFrequency}
                    onChange={(value) => handleFrequencyChange('transactionFrequency', value)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Security Emails */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Shield" size={20} className="text-warning" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Security Emails</h3>
                  <p className="text-sm text-muted-foreground">Login alerts, suspicious activity, and security updates</p>
                </div>
                <button
                  onClick={() => handleToggle('securityEmails')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences?.securityEmails ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences?.securityEmails ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {preferences?.securityEmails && (
                <div className="mt-4">
                  <Select
                    label="Notification Frequency"
                    options={frequencyOptions}
                    value={preferences?.securityFrequency}
                    onChange={(value) => handleFrequencyChange('securityFrequency', value)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Account Emails */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={20} className="text-success" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Account Emails</h3>
                  <p className="text-sm text-muted-foreground">Profile updates, subscription changes, and account notifications</p>
                </div>
                <button
                  onClick={() => handleToggle('accountEmails')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    preferences?.accountEmails ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      preferences?.accountEmails ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {preferences?.accountEmails && (
                <div className="mt-4">
                  <Select
                    label="Notification Frequency"
                    options={frequencyOptions}
                    value={preferences?.accountFrequency}
                    onChange={(value) => handleFrequencyChange('accountFrequency', value)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-foreground mb-1">
              <strong>About Email Notifications</strong>
            </p>
            <p className="text-xs text-muted-foreground">
              All emails are sent via Resend from noreply@boltpay.com. You can opt-out of any category at any time. 
              Security emails are highly recommended to keep your account safe.
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSaveAll}
            loading={saving}
            iconName="Save"
          >
            Save All Preferences
          </Button>
        </div>
      </div>
    </MainLayout>
  );
};

export default EmailNotificationPreferences;